import React, { useMemo, useEffect } from 'react';
import { Alert, Button, Col, Form, InputGroup, Row, Table } from 'react-bootstrap';
import DataApi from '../../../api/DataApi';
import DynamicFieldInput from '../../common/DynamicFieldInput';
import {
  VALUE_SOURCE_DEFAULT,
  VALUE_SOURCE_QUESTION,
  formatDefaultValueLabel,
  formatQuestionOption,
  getLeadFieldMeta,
  isLeadFieldRequired,
  isLookupField,
  normalizeValueSource,
  toDynamicFieldConfig,
} from './fieldMappingUtils';

function formatRowSourceLabel(valueSource) {
  return normalizeValueSource(valueSource) === VALUE_SOURCE_DEFAULT
    ? 'Default value'
    : 'Facebook question';
}

function formatRowValueLabel(row, questions, leadFields) {
  const valueSource = normalizeValueSource(row.valueSource);
  if (valueSource === VALUE_SOURCE_DEFAULT) {
    return formatDefaultValueLabel(row, leadFields);
  }
  if (row.questionKey) {
    const question = (questions || []).find((q) => q.key === row.questionKey);
    return question ? formatQuestionOption(question) : row.questionKey;
  }
  return '—';
}

function flattenViewRecordPayload(data) {
  if (!data || typeof data !== 'object') return {};
  const out = {};
  const take = (fields) => {
    if (!Array.isArray(fields)) return;
    fields.forEach((f) => {
      if (!f) return;
      const name = f.fieldName || f.field_name;
      if (!name) return;
      const v = f.value !== undefined && f.value !== null ? f.value : f.displayValue;
      if (v !== undefined && v !== null && String(v).trim() !== '') {
        out[name] = v;
      }
    });
  };
  if (Array.isArray(data.details)) {
    data.details.forEach((section) => take(section?.fields));
  }
  take(data.otherHiddenFields);
  return out;
}

function buildLookupDisplayLabel(fieldMeta, flat) {
  const displayFields =
    fieldMeta?.display_fields || fieldMeta?.displayFields || fieldMeta?.lookup_search_fields || fieldMeta?.lookupSearchFields || 'name';
  const displayKeys = String(displayFields)
    .split(',')
    .map((key) => key.trim())
    .filter(Boolean);
  return displayKeys
    .map((key) => (flat[key] != null ? String(flat[key]).trim() : ''))
    .filter(Boolean)
    .join(' ');
}

function DefaultValueInput({ row, leadFields, readOnly, onChange }) {
  const fieldMeta = useMemo(
    () => toDynamicFieldConfig(getLeadFieldMeta(leadFields, row.leadField)),
    [leadFields, row.leadField]
  );

  useEffect(() => {
    if (!isLookupField(fieldMeta)) return;
    const recordId = row.defaultValue != null ? String(row.defaultValue).trim() : '';
    const display = row.defaultDisplayValue != null ? String(row.defaultDisplayValue).trim() : '';
    const referenceObject = fieldMeta?.reference_object || fieldMeta?.referenceObject;
    if (!recordId || display || !referenceObject) return;

    let cancelled = false;
    (async () => {
      try {
        const dataApi = new DataApi(`object/view/${referenceObject}`);
        const res = await dataApi.fetchById(recordId);
        const payload = res?.data?.data;
        if (cancelled || !payload) return;
        const label = buildLookupDisplayLabel(fieldMeta, flattenViewRecordPayload(payload));
        if (label) {
          onChange(recordId, label);
        }
      } catch {
        /* missing record / permission */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fieldMeta, row.defaultValue, row.defaultDisplayValue]);

  if (readOnly && !isLookupField(fieldMeta)) {
    return <span>{formatDefaultValueLabel(row, leadFields)}</span>;
  }

  return (
    <DynamicFieldInput
      field={fieldMeta}
      value={row.defaultValue || ''}
      displayValue={row.defaultDisplayValue || ''}
      onChange={(value, displayValue) => {
        if (readOnly) return;
        if (displayValue !== undefined) {
          onChange(value, displayValue);
          return;
        }
        onChange(value);
      }}
      disabled={readOnly}
      size="sm"
      placeholder="Enter default value"
    />
  );
}

const FieldMappingEditor = ({
  rows,
  onRowsChange,
  questions,
  leadFields,
  loadingQuestions,
  questionsError,
  webhookUrl,
  onCopyWebhook,
  readOnly = false,
}) => {
  const availableLeadFields = useMemo(() => {
    const used = new Set((rows || []).map((row) => row.leadField));
    return (leadFields || []).filter((field) => {
      const name = field.field_name || field.fieldName;
      return name && !used.has(name);
    });
  }, [leadFields, rows]);

  const updateRow = (rowId, patch) => {
    onRowsChange(
      (rows || []).map((row) => (row.id === rowId ? { ...row, ...patch } : row))
    );
  };

  const handleValueSourceChange = (rowId, valueSource) => {
    const source = normalizeValueSource(valueSource);
    if (source === VALUE_SOURCE_DEFAULT) {
      updateRow(rowId, {
        valueSource: VALUE_SOURCE_DEFAULT,
        questionKey: '',
        questionLabel: '',
        questionId: '',
        questionType: '',
      });
      return;
    }
    updateRow(rowId, {
      valueSource: VALUE_SOURCE_QUESTION,
      defaultValue: '',
      defaultDisplayValue: '',
    });
  };

  const handleQuestionChange = (rowId, questionKey) => {
    const question = (questions || []).find((q) => q.key === questionKey);
    updateRow(rowId, {
      questionKey: questionKey || '',
      questionLabel: question?.label || '',
      questionId: question?.id || '',
      questionType: question?.type || '',
    });
  };

  const handleUniqueChange = (rowId, checked) => {
    onRowsChange(
      (rows || []).map((row) => ({
        ...row,
        isUnique: row.id === rowId ? checked : checked ? false : row.isUnique,
      }))
    );
  };

  const handleAddField = (fieldName) => {
    if (!fieldName) return;
    const field = (leadFields || []).find(
      (f) => (f.field_name || f.fieldName) === fieldName
    );
    if (!field) return;
    onRowsChange([
      ...(rows || []),
      {
        id: `custom-${fieldName}-${Date.now()}`,
        leadField: field.field_name || field.fieldName,
        leadFieldLabel: field.field_label || field.fieldLabel || fieldName,
        questionKey: '',
        questionLabel: '',
        questionId: '',
        questionType: '',
        required: isLeadFieldRequired(field),
        isDefault: false,
        isUnique: false,
        valueSource: VALUE_SOURCE_QUESTION,
        defaultValue: '',
        defaultDisplayValue: '',
      },
    ]);
  };

  const handleRemoveRow = (rowId) => {
    onRowsChange((rows || []).filter((row) => row.id !== rowId));
  };

  return (
    <div>
      {webhookUrl && (
        <Alert variant="light" className="border mb-3">
          <div className="fw-semibold mb-1">Webhook URL</div>
          <InputGroup size="sm">
            <Form.Control readOnly value={webhookUrl} />
            <Button variant="outline-secondary" onClick={() => onCopyWebhook?.(webhookUrl)}>
              Copy
            </Button>
          </InputGroup>
          <Form.Text className="text-muted">
            Share this URL with WatConnect / Meta to receive lead form submissions.
          </Form.Text>
        </Alert>
      )}

      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="fw-semibold">
          <i className="fa-solid fa-link me-2 text-muted" />
          Field Mappings
          {readOnly ? (
            <span className="badge bg-secondary-subtle text-secondary ms-2 fw-normal">
              View
            </span>
          ) : (
            <span className="badge bg-primary-subtle text-primary ms-2 fw-normal">
              Edit
            </span>
          )}
        </div>
      </div>

      {questionsError && (
        <Alert variant="warning" className="mb-3">
          {questionsError}
        </Alert>
      )}

      <div className="table-responsive border rounded">
        <Table className="mb-0 align-middle">
          <thead className="table-light">
            <tr>
              <th style={{ width: '24%' }}>CRM LEAD FIELD</th>
              <th style={{ width: '16%' }}>SOURCE</th>
              <th style={{ width: '34%' }}>VALUE</th>
              <th style={{ width: '12%' }}>UNIQUE</th>
              {!readOnly ? <th style={{ width: '14%' }}>ACTION</th> : null}
            </tr>
          </thead>
          <tbody>
            {(rows || []).length === 0 ? (
              <tr>
                <td colSpan={readOnly ? 4 : 5} className="text-muted text-center py-4">
                  No mapping rows configured.
                </td>
              </tr>
            ) : (
              (rows || []).map((row) => {
                const valueSource = normalizeValueSource(row.valueSource);
                const usesQuestion = valueSource === VALUE_SOURCE_QUESTION;

                return (
                  <tr key={row.id}>
                    <td>
                      <div className="fw-semibold">
                        {row.leadFieldLabel || row.leadField}
                        {row.required ? <span className="text-danger ms-1">*</span> : null}
                      </div>
                      <div className="small text-muted">field: {row.leadField}</div>
                    </td>
                    <td>
                      {readOnly ? (
                        formatRowSourceLabel(valueSource)
                      ) : (
                        <Form.Select
                          size="sm"
                          value={valueSource}
                          onChange={(e) => handleValueSourceChange(row.id, e.target.value)}
                        >
                          <option value={VALUE_SOURCE_QUESTION}>Facebook question</option>
                          <option value={VALUE_SOURCE_DEFAULT}>Default value</option>
                        </Form.Select>
                      )}
                    </td>
                    <td>
                      {usesQuestion ? (
                        readOnly ? (
                          formatRowValueLabel(row, questions, leadFields)
                        ) : (
                          <Form.Select
                            size="sm"
                            value={row.questionKey || ''}
                            disabled={loadingQuestions}
                            onChange={(e) => handleQuestionChange(row.id, e.target.value)}
                          >
                            <option value="">-- Select Facebook Question --</option>
                            {(questions || []).map((question) => (
                              <option key={question.key || question.id} value={question.key}>
                                {formatQuestionOption(question)}
                              </option>
                            ))}
                          </Form.Select>
                        )
                      ) : (
                        <DefaultValueInput
                          row={row}
                          leadFields={leadFields}
                          readOnly={readOnly}
                          onChange={(value, displayValue) =>
                            updateRow(row.id, {
                              defaultValue: value,
                              ...(displayValue !== undefined
                                ? { defaultDisplayValue: displayValue }
                                : {}),
                            })
                          }
                        />
                      )}
                    </td>
                    <td>
                      {readOnly ? (
                        row.isUnique ? 'Yes' : 'No'
                      ) : (
                        <Form.Check
                          type="checkbox"
                          label="Unique"
                          checked={!!row.isUnique}
                          onChange={(e) => handleUniqueChange(row.id, e.target.checked)}
                        />
                      )}
                    </td>
                    {!readOnly ? (
                      <td>
                        {!row.isDefault ? (
                          <Button
                            variant="link"
                            className="text-danger p-0 text-decoration-none"
                            onClick={() => handleRemoveRow(row.id)}
                          >
                            Remove
                          </Button>
                        ) : (
                          <span className="text-muted small">—</span>
                        )}
                      </td>
                    ) : null}
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      {!readOnly && (
        <Row className="mt-3 g-2 align-items-center">
          <Col md={6}>
            <Form.Select
              size="sm"
              defaultValue=""
              onChange={(e) => {
                handleAddField(e.target.value);
                e.target.value = '';
              }}
            >
              <option value="">-- Add Mapped CRM Lead Field --</option>
              {availableLeadFields.map((field) => {
                const name = field.field_name || field.fieldName;
                const label = field.field_label || field.fieldLabel || name;
                return (
                  <option key={name} value={name}>
                    {label} ({name})
                  </option>
                );
              })}
            </Form.Select>
          </Col>
        </Row>
      )}
    </div>
  );
};

/** Shown above Save — one unique mapped field enables lead upsert. */
export function UniqueFieldUpsertHint() {
  return (
    <p className="text-muted small mb-0">
      Mark one field as <strong>Unique</strong> to upsert leads; otherwise a new lead is
      created every time. Map from a Facebook question or set a fixed default (e.g. lead
      source).
    </p>
  );
}

export default FieldMappingEditor;
